import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getApiBase } from '../utils/apiConfig';
import { PROVINCES, citiesFor } from '../features/onboarding/geography.js';
import { SERVICE_OPTIONS } from '../features/onboarding/onboardingMapping.js';
import MovdInput from '../design-system/MovdInput.jsx';
import MovdButton from '../design-system/MovdButton.jsx';
import MovdNotice from '../design-system/MovdNotice.jsx';
import { movdTokens } from '../design-system/movdTokens.js';

const stats = [
  { value: 'Free', label: 'To Join' },
  { value: 'Inbound', label: 'Booking Requests' },
  { value: 'Fast', label: 'Stripe Payouts' },
  { value: '24/7', label: 'Support' },
];

const whyMovd = [
  { icon: '\uD83D\uDCB0', title: 'More Bookings', body: 'Get matched with customers ready to book in your service area — no cold calling or chasing leads.' },
  { icon: '\u26A1', title: 'Fast Payouts', body: 'Stripe-powered payments deposited directly to your bank. Get paid quickly, every time.' },
  { icon: '\u2705', title: 'Verified Badge', body: 'Movers who complete our verification process receive a Verified badge on their profile to build customer confidence.' },
  { icon: '\uD83D\uDCCA', title: 'Business Analytics', body: 'Track revenue, bookings, and reviews in a clean dashboard built for movers.' },
  { icon: '\uD83D\uDC65', title: 'Team Tools', body: 'Manage your crew, assign jobs, and coordinate schedules all in one place.' },
  { icon: '\uD83C\uDFA7', title: 'Dedicated Support', body: 'A real partner success team — not a chatbot — available whenever you need help.' },
];

const steps = [
  { num: '01', title: 'Apply in minutes', body: 'Fill out your company info, service areas, and pricing. Takes less than 5 minutes.' },
  { num: '02', title: 'Get verified', body: "Our team reviews your application and confirms your licence and insurance details. We'll be in touch once reviewed." },
  { num: '03', title: 'Start earning', body: 'Go live on the platform, receive booking requests, and grow your business.' },
];

const cardStyle = { padding: 28, height: '100%', borderRadius: 12, border: '1.5px solid #E2E8F0', background: '#fff' };

export default function BecomePartner() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState(null);
  const [formData, setFormData] = useState({
    email: '', password: '', companyName: '', phone: '', yearsInBusiness: '',
    homeProvince: '', homeCity: '',
    services: { residential: true, commercial: false, longDistance: false, packing: false, specialty: false, assembly: false },
  });

  const set = field => e => setFormData(p => ({ ...p, [field]: e.target.value }));
  const toggleService = id => setFormData(p => ({ ...p, services: { ...p.services, [id]: !p.services[id] } }));

  const scrollToForm = () => document.getElementById('partner-form')?.scrollIntoView({ behavior: 'smooth' });

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${getApiBase()}/movers/register`, {
        name: formData.companyName,
        email: formData.email,
        password: formData.password,
        phone: formData.phone,
        yearsInBusiness: formData.yearsInBusiness || undefined,
        provinces: formData.homeProvince ? [formData.homeProvince] : [],
        locations: formData.homeCity ? [formData.homeCity] : [],
        longDistanceMoves: formData.services.longDistance,
        residentialMoves: formData.services.residential,
        commercialMoves: formData.services.commercial,
        services: ['moving', ...(formData.services.packing ? ['packing'] : []), ...(formData.services.specialty ? ['specialty'] : []), ...(formData.services.assembly ? ['assembly'] : [])],
      }, { withCredentials: true });

      // Keep the returned token so the partner portal skips the login screen and
      // drops the new mover straight into the guided onboarding wizard.
      const token = res?.data?.token;
      if (token) {
        localStorage.setItem('token', token);
        localStorage.setItem('userData', JSON.stringify({ email: formData.email, name: formData.companyName, role: 'mover' }));
      }

      setNotice({ severity: 'success', text: 'Account created! Opening your setup wizard…' });
      setTimeout(() => navigate('/mover-portal'), 1200);
    } catch (err) {
      setNotice({ severity: 'error', text: err?.response?.data?.error || 'Registration failed. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ background: '#F8FAFC', minHeight: '100vh', overflowX: 'hidden' }}>
      <div style={{ minHeight: '70vh', background: 'linear-gradient(135deg, #0F172A 0%, #1E3A8A 55%, #1E293B 100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '64px 20px', color: '#fff' }}>
        <div style={{ maxWidth: 780 }}>
          <span style={{ display: 'inline-block', background: 'rgba(37,99,235,0.2)', color: '#93C5FD', border: '1px solid rgba(147,197,253,0.25)', fontWeight: 700, letterSpacing: '.08em', fontSize: 12, padding: '6px 14px', borderRadius: 20, marginBottom: 24 }}>FOR MOVING COMPANIES</span>
          <h1 style={{ fontWeight: 900, margin: '0 0 24px', fontSize: 'clamp(2.2rem, 6vw, 4.2rem)', lineHeight: 1.1, letterSpacing: '-.02em' }}>Grow your moving<br />business with Movd</h1>
          <p style={{ margin: '0 0 40px', color: 'rgba(255,255,255,.75)', fontSize: 'clamp(1.05rem, 2vw, 1.3rem)', lineHeight: 1.6, maxWidth: 580, marginLeft: 'auto', marginRight: 'auto' }}>Join 500+ verified Canadian movers getting more bookings, faster payments, and real business growth — all in one platform.</p>
          <div style={{ display: 'flex', gap: 16, justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
            <MovdButton onClick={scrollToForm}>Apply now — it&apos;s free →</MovdButton>
            <button type="button" onClick={() => navigate('/mover-portal')} style={{ background: 'transparent', border: 'none', color: 'rgba(255,255,255,.6)', fontWeight: 600, cursor: 'pointer' }}>Already a partner? Sign in →</button>
          </div>
        </div>
      </div>

      <div style={{ background: '#1E293B', padding: '32px 20px' }}>
        <div style={{ maxWidth: 1120, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))' }}>
          {stats.map(s => (
            <div key={s.label} style={{ textAlign: 'center', padding: '16px 8px' }}>
              <p style={{ margin: 0, fontWeight: 800, fontSize: 28, color: '#60A5FA' }}>{s.value}</p>
              <p style={{ margin: 0, color: 'rgba(255,255,255,.45)', fontWeight: 600, fontSize: 13 }}>{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      <div style={{ padding: 'clamp(48px, 8vw, 96px) 20px', background: '#F8FAFC' }}>
        <div style={{ maxWidth: 1120, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <span style={{ display: 'inline-block', background: '#EFF6FF', color: '#2563EB', fontWeight: 700, letterSpacing: '.08em', fontSize: 12, padding: '6px 14px', borderRadius: 20, marginBottom: 16 }}>HOW IT WORKS</span>
            <h2 style={{ margin: 0, fontWeight: 800, color: '#0F172A', fontSize: 'clamp(1.7rem, 4vw, 2.5rem)' }}>Up and running in 3 steps</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 24 }}>
            {steps.map(s => (
              <div key={s.num} style={{ ...cardStyle, position: 'relative' }}>
                <span style={{ position: 'absolute', top: 20, right: 24, fontSize: 44, fontWeight: 900, color: '#F1F5F9', lineHeight: 1 }}>{s.num}</span>
                <h3 style={{ margin: '0 0 12px', fontWeight: 800, color: '#0F172A', fontSize: 20, paddingRight: 48 }}>{s.title}</h3>
                <p style={{ margin: 0, color: '#64748B', lineHeight: 1.7 }}>{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{ padding: 'clamp(48px, 8vw, 96px) 20px', background: '#0F172A' }}>
        <div style={{ maxWidth: 1120, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <span style={{ display: 'inline-block', background: 'rgba(37,99,235,0.15)', color: '#60A5FA', fontWeight: 700, letterSpacing: '.08em', fontSize: 12, padding: '6px 14px', borderRadius: 20, border: '1px solid rgba(96,165,250,0.2)', marginBottom: 16 }}>WHAT YOU GET</span>
            <h2 style={{ margin: 0, fontWeight: 800, color: '#fff', fontSize: 'clamp(1.7rem, 4vw, 2.5rem)' }}>Everything you need to grow</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 20 }}>
            {whyMovd.map(item => (
              <div key={item.title} style={{ padding: 28, borderRadius: 12, border: '1px solid rgba(255,255,255,.07)', background: 'rgba(255,255,255,.04)' }}>
                <div style={{ fontSize: 28, marginBottom: 12 }} aria-hidden="true">{item.icon}</div>
                <h3 style={{ margin: '0 0 6px', fontWeight: 700, color: '#fff', fontSize: 17 }}>{item.title}</h3>
                <p style={{ margin: 0, color: 'rgba(255,255,255,.5)', lineHeight: 1.7, fontSize: 14 }}>{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div id="partner-form" style={{ padding: 'clamp(48px, 8vw, 96px) 20px', background: '#F8FAFC' }}>
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <span style={{ display: 'inline-block', background: '#EFF6FF', color: '#2563EB', fontWeight: 700, letterSpacing: '.08em', fontSize: 12, padding: '6px 14px', borderRadius: 20, marginBottom: 16 }}>APPLY NOW</span>
            <h2 style={{ margin: '0 0 12px', fontWeight: 800, color: '#0F172A', fontSize: 'clamp(1.7rem, 4vw, 2.5rem)' }}>Create your partner account</h2>
            <p style={{ margin: 0, color: '#64748B', maxWidth: 520, marginLeft: 'auto', marginRight: 'auto' }}>Free to join. Once your account is created, a short guided setup walks you through pricing, service area, and availability.</p>
          </div>

          <div style={{ padding: 'clamp(20px, 4vw, 40px)', borderRadius: 12, border: '1.5px solid #E2E8F0', background: '#fff' }}>
            <form onSubmit={handleSubmit} noValidate style={{ display: 'grid', gap: 32 }}>
              <div>
                <p style={{ margin: '0 0 12px', color: '#2563EB', fontWeight: 700, letterSpacing: '.1em', fontSize: 12, textTransform: 'uppercase' }}>Account</p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
                  <MovdInput label="Email address" type="email" value={formData.email} onChange={set('email')} required />
                  <MovdInput label="Password" type="password" value={formData.password} onChange={set('password')} required />
                </div>
              </div>

              <div>
                <p style={{ margin: '0 0 12px', color: '#2563EB', fontWeight: 700, letterSpacing: '.1em', fontSize: 12, textTransform: 'uppercase' }}>Company info</p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 16 }}>
                  <MovdInput label="Company name" value={formData.companyName} onChange={set('companyName')} required />
                  <MovdInput label="Phone number" value={formData.phone} onChange={set('phone')} required />
                  <MovdInput label="Years in business" type="number" value={formData.yearsInBusiness} onChange={set('yearsInBusiness')} />
                  <label style={{ display: 'grid', gap: 8, color: movdTokens.color.tealSoft, fontSize: 11, fontWeight: 900, letterSpacing: '.14em', textTransform: 'uppercase' }}>
                    <span>Home province</span>
                    <select value={formData.homeProvince} onChange={e => setFormData(p => ({ ...p, homeProvince: e.target.value, homeCity: '' }))} required style={{ minHeight: 52, borderRadius: movdTokens.radius.control, border: '1px solid rgba(0,0,0,.15)', padding: '0 12px', font: 'inherit', fontSize: 15, fontWeight: 600, textTransform: 'none', color: movdTokens.color.ink }}>
                      <option value="" disabled>Select a province</option>
                      {PROVINCES.map(p => <option key={p.code} value={p.code}>{p.name}</option>)}
                    </select>
                  </label>
                  <label style={{ display: 'grid', gap: 8, color: movdTokens.color.tealSoft, fontSize: 11, fontWeight: 900, letterSpacing: '.14em', textTransform: 'uppercase' }}>
                    <span>Home city</span>
                    <select value={formData.homeCity} onChange={set('homeCity')} required disabled={!formData.homeProvince} style={{ minHeight: 52, borderRadius: movdTokens.radius.control, border: '1px solid rgba(0,0,0,.15)', padding: '0 12px', font: 'inherit', fontSize: 15, fontWeight: 600, textTransform: 'none', color: movdTokens.color.ink }}>
                      <option value="" disabled>Select a city</option>
                      {citiesFor(formData.homeProvince).map(city => <option key={city} value={city}>{city}</option>)}
                    </select>
                  </label>
                </div>
                <p style={{ margin: '0 0 8px', color: '#475569', fontWeight: 700, fontSize: 14 }}>Services you offer</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px' }}>
                  {SERVICE_OPTIONS.map(option => (
                    <label key={option.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, width: 'calc(33% - 11px)', minWidth: 140 }}>
                      <input type="checkbox" checked={formData.services[option.id]} onChange={() => toggleService(option.id)} />
                      {option.label}
                    </label>
                  ))}
                </div>
              </div>

              <MovdNotice tone="info">You&apos;ll set your rates, full service area, and availability next in the guided setup — it takes about 5 minutes.</MovdNotice>

              <MovdButton type="submit" loading={loading} style={{ width: '100%' }}>Create account & start setup</MovdButton>

              <p style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center', color: movdTokens.color.content.secondary, fontSize: 12, margin: 0 }}><span aria-hidden="true">🛡</span> Your information is secure and never shared</p>

              <p style={{ textAlign: 'center', color: movdTokens.color.content.secondary, fontSize: 14, margin: 0 }}>
                Already have an account?{' '}
                <button type="button" onClick={() => navigate('/mover-portal')} style={{ background: 'transparent', border: 'none', color: '#2563EB', fontWeight: 700, cursor: 'pointer', padding: 0 }}>Sign in here</button>
              </p>
            </form>
          </div>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center', marginTop: 32 }}>
            {['Free to join', 'No monthly fees', 'Cancel anytime', 'Verified & Insured'].map(label => (
              <span key={label} style={{ background: '#fff', border: '1px solid #E2E8F0', fontWeight: 600, color: '#374151', borderRadius: 20, padding: '6px 14px', fontSize: 13 }}>✓ {label}</span>
            ))}
          </div>
        </div>
      </div>

      {notice && <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 1500, minWidth: 280 }}><MovdNotice tone={notice.severity}>{notice.text}</MovdNotice></div>}
    </div>
  );
}
